/**
 * themeStore — Dark/light mode toggle with localStorage persistence.
 *
 * Architecture note (2026-07-02): This is the web-dashboard bespoke implementation
 * of the theme-store pattern.  It intentionally differs from the universal
 * `createThemeStore` factory in `@plerity/shared-stores/theme`:
 *
 *   - This store uses `createAppStore` (Zustand + devtools), reads/writes
 *     `localStorage` directly, and applies the theme to `document.documentElement`
 *     immediately on every change.  The state keys are `theme`/`resolvedTheme`/
 *     `setTheme`/`toggleTheme`, which match the 1,100+ web-dashboard consumers.
 *
 *   - The universal factory (`createThemeStore`) is adapter-injected (no platform
 *     imports), uses the persist middleware, supports OS-change subscription and
 *     per-org brand palette overrides, and is shared by the React Native mobile
 *     apps.  Its state keys differ (`mode`/`resolvedScheme`/`setMode`/`toggle`).
 *
 * Consolidating onto the factory would require a fleet-wide API rename across all
 * web-dashboard shells and SaaS apps.  The split is intentional and stable — the
 * two implementations converge in behavior but not in API.  See:
 *   packages/universal/shared-stores/src/theme/index.ts
 */

import { createAppStore } from "./createAppStore";

type Theme = "light" | "dark" | "system";

interface ThemeState {
  theme: Theme;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(theme: Theme): "light" | "dark" {
  return theme === "system" ? getSystemTheme() : theme;
}

function getInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem("dashboard-theme");
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    // localStorage unavailable
  }
  return "system";
}

function applyThemeToDocument(resolved: "light" | "dark") {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolved === "dark");
}

const initialTheme = getInitialTheme();
const initialResolved = resolveTheme(initialTheme);
applyThemeToDocument(initialResolved);

export const useThemeStore = createAppStore<ThemeState>("theme-store", (set) => ({
  theme: initialTheme,
  resolvedTheme: initialResolved,

  setTheme: (theme) => {
    const resolved = resolveTheme(theme);
    try {
      localStorage.setItem("dashboard-theme", theme);
    } catch {
      // ignore
    }
    applyThemeToDocument(resolved);
    set({ theme, resolvedTheme: resolved });
  },

  toggleTheme: () => {
    set((state) => {
      const next: Theme = state.resolvedTheme === "dark" ? "light" : "dark";
      const resolved = resolveTheme(next);
      try {
        localStorage.setItem("dashboard-theme", next);
      } catch {
        // ignore
      }
      applyThemeToDocument(resolved);
      return { theme: next, resolvedTheme: resolved };
    });
  },
}));

// Listen for OS-level theme changes and update when user preference is "system"
if (typeof window !== "undefined") {
  const mql = window.matchMedia("(prefers-color-scheme: dark)");
  mql.addEventListener("change", () => {
    const state = useThemeStore.getState();
    if (state.theme === "system") {
      const resolved = getSystemTheme();
      applyThemeToDocument(resolved);
      useThemeStore.setState({ resolvedTheme: resolved });
    }
  });
}
